import { PaymentStatusDto } from '@api-client';

/**
 * Wording and status rules for the subscription popup (ZEY-3046).
 *
 * Tier1 (before expiry) shows nothing — that warning goes out by email. Tier2 (expired) shows a
 * blocking popup. When it shows, its countdown and how long it stays away afterwards are decided on
 * the server (ZEY-3181 PaymentReminder announcement, from the billing policy).
 */

/** Used until the settings response carries `lockoutDaysLate`. */
export const DEFAULT_LOCKOUT_DAYS_LATE = 14;
export const BILLING_EMAIL = 'billing@zeymo.com';

/** `lockoutDaysLate` is being added to the DTO; read it without depending on the regenerated client. */
export type PaymentStatusWithLockout = PaymentStatusDto & { lockoutDaysLate?: number | null };

/** The popup cadence is being added to the DTO (ZEY-3061); read it without depending on the regenerated client. */
export type PaymentStatusWithCadence = PaymentStatusDto & {
    popupEveryMinutes?: number | null;
    popupMinSeconds?: number | null;
};

export function isReminderStatus(status: PaymentStatusDto | null | undefined): boolean {
    return status?.status === 'Tier2';
}

export interface LockInfo {
    /** Days past the due date; negative while it is still ahead. */
    daysLate: number;
    /** The day the account locks; -1 when no lock is coming (Lock off, or an admin forced a non-lock signal). */
    lockoutDaysLate: number;
    /** True when no lock is coming — the popup then promises no lock date (ZEY-3061). */
    noLock: boolean;
    /** True once the lockout threshold is reached (possible while enforcement is dark). */
    pastDue: boolean;
    lockDate: Date;
    daysUntilLock: number;
}

export function lockInfo(status: PaymentStatusDto | null | undefined, now: Date): LockInfo {
    const daysLate = Math.floor(status?.daysLate ?? 0);
    const configured = (status as PaymentStatusWithLockout | null | undefined)?.lockoutDaysLate;
    const noLock = typeof configured === 'number' && configured < 0;
    const lockoutDaysLate = noLock ? -1 : typeof configured === 'number' && configured > 0 ? configured : DEFAULT_LOCKOUT_DAYS_LATE;
    const daysUntilLock = lockoutDaysLate - daysLate;
    const lockDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysUntilLock);
    return { daysLate, lockoutDaysLate, noLock, pastDue: !noLock && daysUntilLock <= 0, lockDate, daysUntilLock };
}

/** Days-late values below this mean "nothing is due" (the server's NotLate sentinel), not a real date. */
const NOTHING_DUE_BELOW = -3650;

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;

export function formatLockDate(d: Date): string {
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
}

export function reminderMessage(status: PaymentStatusDto | null | undefined, now: Date): string {
    const info = lockInfo(status, now);
    if (!Number.isFinite(info.daysLate) || info.daysLate < NOTHING_DUE_BELOW) {
        // Forced popup on a tenant who owes nothing (ZEY-3061): no date to quote.
        return 'Your Zeymo subscription needs your attention. Please contact billing.';
    }
    const head = info.daysLate < 0
        ? info.daysLate === -1
            ? 'Your Zeymo subscription expires tomorrow.'
            : `Your Zeymo subscription expires in ${plural(-info.daysLate, 'day')}.`
        : info.daysLate === 0
            ? 'Your Zeymo subscription expired today.'
            : `Your Zeymo subscription expired ${plural(info.daysLate, 'day')} ago.`;
    if (info.noLock) {
        return info.daysLate < 0 ? head : `${head} Please renew your subscription.`;
    }
    if (info.pastDue) {
        return `${head} Your account is past due and will be locked at any time.`;
    }
    return `${head} Your account will be locked on ${formatLockDate(info.lockDate)} (in ${plural(info.daysUntilLock, 'day')}).`;
}
